import { useQuery } from '@apollo/client';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import {
  GetIsUnreadMeActionablePerformanceChannelsDocument,
  GetIsUnreadMeActionablePerformanceChannelsArtistOfficialDocument,
} from './../__generated__/graphql';
import { GetMeIsUnreadMyNotificationDocument } from '@/__generated__/graphql';
import { IMAGE_PATH } from '@/constants/image-path';
import { PATHS } from '@/constants/path';
import type { PathValues } from '@/constants/path';

/**
 * ログイン後ナビで利用する共通処理
 */

type AuthenticatedNavigationReturn = {
  spNavigationItems: NavigationItem[];
  pcNavigationItems: NavigationItem[];
  unread: boolean;
  hasUnreadChannel: boolean;
  avatar: string;
};

export interface NavigationItem {
  title: string;
  path: PathValues;
  additionalPaths?: string[];
}

export const useAuthenticatedNavigation = (): AuthenticatedNavigationReturn => {
  const { data, error } = useQuery(GetMeIsUnreadMyNotificationDocument);
  const { data: performanceChannelsData, fetchMore: fetchMorePerformance } = useQuery(
    GetIsUnreadMeActionablePerformanceChannelsDocument,
  );
  const { data: artistOfficialChannelsData, fetchMore: fetchMoreArtist } = useQuery(
    GetIsUnreadMeActionablePerformanceChannelsArtistOfficialDocument,
  );
  const t = useTranslations();

  const items = {
    CHANNELS: {
      title: t('common_channels'),
      path: PATHS.CHANNELS,
    },
    FOLLOWING: {
      title: t('common_following'),
      path: PATHS.FOLLOWING,
    },
    DISCOVER: {
      title: t('common_discover'),
      path: PATHS.ROOT,
    },
    NOTIFICATIONS: {
      title: t('common_notifications'),
      path: PATHS.NOTIFICATIONS_ME,
      additionalPaths: [PATHS.NOTIFICATIONS_NEWS, PATHS.NOTIFICATIONS_NEWS_DETAIL],
    },
    SETTINGS: {
      title: t('common_settings'),
      path: PATHS.SETTINGS,
    },
  };

  const spNavigationItems: NavigationItem[] = [
    items.CHANNELS,
    items.FOLLOWING,
    items.DISCOVER,
    items.NOTIFICATIONS,
    items.SETTINGS,
  ];

  const pcNavigationItems: NavigationItem[] = [
    items.DISCOVER,
    items.CHANNELS,
    items.FOLLOWING,
    items.NOTIFICATIONS,
    items.SETTINGS,
  ];

  if (error) {
    //TODO: エラー処理
    console.error(error);
  }

  const unread = !!data?.me?.isUnreadMyNotification;

  const avatar = data?.me?.iconImage?.url || IMAGE_PATH.DEFAULT_USER;

  const { actionablePerformanceChannels } = performanceChannelsData?.me || {};
  const { actionablePerformanceChannelsAsArtistOfficial } = artistOfficialChannelsData?.me || {};
  const performanceEdges = actionablePerformanceChannels?.edges || [];
  const artistEdges = actionablePerformanceChannelsAsArtistOfficial?.edges || [];

  const fetchAllPages = async () => {
    try {
      let performancePageInfo = actionablePerformanceChannels?.pageInfo;
      while (performancePageInfo?.hasNextPage) {
        const {
          data: { me },
        } = await fetchMorePerformance({ variables: { after: performancePageInfo.endCursor } });
        if (!me?.actionablePerformanceChannels) break;
        performancePageInfo = me.actionablePerformanceChannels.pageInfo;
      }

      let artistPageInfo = actionablePerformanceChannelsAsArtistOfficial?.pageInfo;
      while (artistPageInfo?.hasNextPage) {
        const {
          data: { me },
        } = await fetchMoreArtist({ variables: { after: artistPageInfo.endCursor } });
        if (!me?.actionablePerformanceChannelsAsArtistOfficial) break;
        artistPageInfo = me.actionablePerformanceChannelsAsArtistOfficial.pageInfo;
      }
    } catch (error) {
      console.error('Fetch all pages error:', error);
    }
  };

  useEffect(() => {
    const hasNextPage =
      actionablePerformanceChannels?.pageInfo.hasNextPage ||
      actionablePerformanceChannelsAsArtistOfficial?.pageInfo.hasNextPage;
    if (hasNextPage) {
      fetchAllPages();
    }
  }, [actionablePerformanceChannels, actionablePerformanceChannelsAsArtistOfficial]);

  return {
    spNavigationItems,
    pcNavigationItems,
    unread,
    hasUnreadChannel:
      performanceEdges.some(({ node }) => node.hasUnread) ||
      artistEdges.some(({ node }) => node.hasUnread),
    avatar,
  };
};
